import { useEffect, useRef } from "react";

import { cn } from "../index";
import { ScrollArea } from "../ScrollArea";
import { TextField } from "../TextField";
import * as PrimitiveCombobox from "./PrimitiveCombobox";

/* -------------------------------------------------------------------------------------------------
 * Combobox
 * -----------------------------------------------------------------------------------------------*/

type ComboboxProps = PrimitiveCombobox.ComboboxProps;

const Root = PrimitiveCombobox.Root;

Root.displayName = "Combobox";

/* -------------------------------------------------------------------------------------------------
 * Input
 * -----------------------------------------------------------------------------------------------*/

type InputProps = TextField.TextFieldProps;

const Input: React.FC<InputProps> = props => {
  return (
    <PrimitiveCombobox.Anchor asChild>
      <PrimitiveCombobox.Input asChild {...props}>
        <TextField.Root />
      </PrimitiveCombobox.Input>
    </PrimitiveCombobox.Anchor>
  );
};

Input.displayName = "ComboboxInput";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

type ContentProps = React.ComponentProps<typeof PrimitiveCombobox.Content>;

const Content: React.FC<ContentProps> = ({
  align = "start",
  alignOffset = -20,
  sideOffset = 20,
  className,
  children,
  ...props
}) => {
  const { inputValue } = PrimitiveCombobox.useComboboxContext("ComboboxContent");
  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (viewportRef.current) {
        viewportRef.current.scrollTop = 0;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [inputValue]);

  return (
    <PrimitiveCombobox.Portal>
      <PrimitiveCombobox.Content
        data-slot="combobox-content"
        asChild
        align={align}
        alignOffset={alignOffset}
        sideOffset={sideOffset}
        className={cn(
          `p-2.5 flex w-[calc(var(--radix-popover-trigger-width)+40px)] flex-col gap-[5px] rounded-[10px] bg-primary-white shadow-[var(--shadow-elevated)]`,
          className,
        )}
        {...props}
      >
        <ScrollArea viewportRef={viewportRef}>
          <PrimitiveCombobox.List>{children}</PrimitiveCombobox.List>
        </ScrollArea>
      </PrimitiveCombobox.Content>
    </PrimitiveCombobox.Portal>
  );
};

Content.displayName = "ComboboxContent";

/* -------------------------------------------------------------------------------------------------
 * Empty
 * -----------------------------------------------------------------------------------------------*/

type EmptyProps = React.ComponentProps<typeof PrimitiveCombobox.Empty>;

const Empty: React.FC<EmptyProps> = ({ className, ...props }) => {
  return (
    <PrimitiveCombobox.Empty
      className={cn("flex items-center justify-center p-[7.5px] text-base", className)}
      {...props}
    />
  );
};

Empty.displayName = "ComboboxEmpty";

/* -------------------------------------------------------------------------------------------------
 * Group
 * -----------------------------------------------------------------------------------------------*/

type GroupProps = React.ComponentProps<typeof PrimitiveCombobox.Group>;

const Group: React.FC<GroupProps> = props => {
  return <PrimitiveCombobox.Group {...props} />;
};

Group.displayName = "ComboboxGroup";

/* -------------------------------------------------------------------------------------------------
 * Item
 * -----------------------------------------------------------------------------------------------*/

type ItemProps = React.ComponentProps<typeof PrimitiveCombobox.Item>;

const Item: React.FC<ItemProps> = ({ className, ...props }) => (
  <PrimitiveCombobox.Item
    data-slot="combobox-item"
    className={cn(
      `flex w-full items-center rounded-[5px] p-[7.5px] text-base select-none data-[selected=true]:bg-primary-gray`,
      className,
    )}
    {...props}
  />
);

Item.displayName = "ComboboxItem";

/* -------------------------------------------------------------------------------------------------
 * ItemText
 * -----------------------------------------------------------------------------------------------*/

type ItemTextProps = React.ComponentProps<typeof PrimitiveCombobox.ItemText>;

const ItemText = PrimitiveCombobox.ItemText;

ItemText.displayName = "ComboboxItemText";

export { Content, Empty, Group, Input, Item, ItemText, Root };
export type { ComboboxProps, ContentProps, EmptyProps, GroupProps, InputProps, ItemProps, ItemTextProps };
